import React, { useState, useEffect } from 'react';

const IoTDashboard = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [sensorHistory, setSensorHistory] = useState([]);
  const [liveData, setLiveData] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connected');

  useEffect(() => {
    // Simüle edilmiş cihaz listesi ve canlı veri
    const mockDevices = [
      {
        device_id: 'ESP32-PARK-001',
        park_id: 'cankaya-100yil',
        park_name: '100. Yıl Parkı',
        sensor_type: 'soil_moisture',
        status: 'online',
        last_seen: new Date().toISOString(),
        battery_v: 3.72,
        rssi: -54,
        firmware: '1.0.2',
        location: { lat: 39.8903, lng: 32.8612 }
      },
      {
        device_id: 'ESP32-PARK-002',
        park_id: 'cankaya-botanik',
        park_name: 'Botanik Parkı',
        sensor_type: 'soil_moisture',
        status: 'online',
        last_seen: new Date(Date.now() - 180000).toISOString(),
        battery_v: 3.65,
        rssi: -67,
        firmware: '1.0.2',
        location: { lat: 39.8745, lng: 32.7324 }
      },
      {
        device_id: 'ASKI-IOT-3485',
        park_id: 'aski-cloud',
        park_name: 'ASKİ Akıllı Sayaç (Pilot)',
        sensor_type: 'water_flow',
        status: 'online',
        last_seen: new Date(Date.now() - 60000).toISOString(),
        battery_v: null,
        rssi: null,
        firmware: 'ASKI-v3',
        location: { lat: 39.9208, lng: 32.8541 }
      }
    ];

    setDevices(mockDevices);
    setSelectedDevice(mockDevices[0]);

    // Deterministik sensör geçmişi (sabit seed ile her açılışta aynı grafik)
    const seededRandom = (seed) => {
      const x = Math.sin(seed * 9301 + 49297) * 233280;
      return x - Math.floor(x);
    };

    const history = [];
    const now = Date.now();
    for (let i = 47; i >= 0; i--) {
      const time = new Date(now - i * 1800000);
      const hour = time.getHours();
      const seed = i * 7 + hour;
      const baseMoisture = hour >= 6 && hour <= 18
        ? 35 + seededRandom(seed) * 10
        : 50 + seededRandom(seed + 100) * 8;
      history.push({
        timestamp: time.toISOString(),
        moisture_pct: Math.round(baseMoisture * 10) / 10,
        temperature: Math.round((18 + (hour >= 10 && hour <= 16 ? 12 : 3) + seededRandom(seed + 200) * 3) * 10) / 10,
        raw_adc: Math.round(2200 + seededRandom(seed + 300) * 600),
        status: baseMoisture > 50 ? 'islak' : baseMoisture > 30 ? 'normal' : 'kuru'
      });
    }
    setSensorHistory(history);
    setLiveData(history[history.length - 1]);

    // Canlı güncelleme: sadece son veri kartını günceller (grafik sabit kalır)
    const interval = setInterval(() => {
      const hour = new Date().getHours();
      const t = Date.now();
      const seed = Math.floor(t / 5000);
      const seeded = (s) => { const x = Math.sin(s * 9301 + 49297) * 233280; return x - Math.floor(x); };
      const m = hour >= 6 && hour <= 18
        ? 33 + seeded(seed) * 12
        : 48 + seeded(seed) * 10;
      const newData = {
        timestamp: new Date().toISOString(),
        moisture_pct: Math.round(m * 10) / 10,
        temperature: Math.round((20 + seeded(seed + 50) * 8) * 10) / 10,
        raw_adc: Math.round(2000 + seeded(seed + 100) * 800),
        status: m > 50 ? 'islak' : m > 30 ? 'normal' : 'kuru'
      };
      setLiveData(newData);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'cok_islak': return '#1d4ed8';
      case 'islak': return '#2563eb';
      case 'normal': return '#16a34a';
      case 'kuru': return '#f59e0b';
      case 'cok_kuru': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'cok_islak': return 'Çok Islak';
      case 'islak': return 'Islak';
      case 'normal': return 'Normal';
      case 'kuru': return 'Kuru';
      case 'cok_kuru': return 'Çok Kuru';
      default: return status;
    }
  };

  const maxMoisture = Math.max(...sensorHistory.map(h => h.moisture_pct), 1);

  return (
    <div style={{ padding: '24px' }}>
      {/* Başlık */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' }}>
            IoT Cihaz Yönetimi
          </h2>
          <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
            Toprak Nem Sensörleri • ESP32 • ASKİ Cloud Entegrasyonu
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: connectionStatus === 'connected' ? '#16a34a' : '#dc2626',
            animation: 'pulse 2s infinite'
          }} />
          <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>
            {devices.filter(d => d.status === 'online').length} Cihaz Çevrimiçi
          </span>
        </div>
      </div>

      {/* Cihaz Listesi */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '24px' }}>
        {devices.map(device => (
          <div
            key={device.device_id}
            onClick={() => setSelectedDevice(device)}
            style={{
              background: selectedDevice?.device_id === device.device_id ? '#eff6ff' : '#fff',
              border: `2px solid ${selectedDevice?.device_id === device.device_id ? '#2563eb' : '#e5e7eb'}`,
              borderRadius: '12px', padding: '16px', cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>{device.park_name}</div>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: device.status === 'online' ? '#16a34a' : '#dc2626'
              }} />
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>{device.device_id}</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              {device.sensor_type === 'soil_moisture' ? 'Toprak Nem Sensörü' : 'Su Akış Sayacı'}
            </div>
            {device.battery_v && (
              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                <span style={{ color: device.battery_v > 3.5 ? '#16a34a' : '#f59e0b' }}>
                  ⚡ {device.battery_v}V
                </span>
                <span style={{ color: '#9ca3af', marginLeft: '8px' }}>📶 {device.rssi} dBm</span>
              </div>
            )}
            {device.sensor_type === 'water_flow' && (
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#2563eb', fontWeight: '600' }}>
                ☁️ Cloud Bağlantı
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Canlı Veri Kartları */}
      {liveData && selectedDevice?.sensor_type === 'soil_moisture' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: getStatusColor(liveData.status) }}>
              %{liveData.moisture_pct}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Toprak Nemi</div>
            <div style={{
              marginTop: '6px', fontSize: '10px', fontWeight: '700', padding: '2px 8px',
              borderRadius: '10px', display: 'inline-block',
              background: getStatusColor(liveData.status) + '20',
              color: getStatusColor(liveData.status)
            }}>
              {getStatusLabel(liveData.status)}
            </div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#ea580c' }}>
              {liveData.temperature}°
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Sıcaklık (°C)</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: '800', color: '#7c3aed' }}>
              {liveData.raw_adc}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Ham ADC</div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginTop: '8px' }}>
              {new Date(liveData.timestamp).toLocaleTimeString('tr-TR')}
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Son Güncelleme</div>
            <div style={{ fontSize: '10px', color: '#16a34a', marginTop: '4px' }}>● Canlı (5s)</div>
          </div>
        </div>
      )}

      {/* Nem Grafiği (Son 24 Saat) */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '16px', color: '#1a1a2e' }}>
          Son 24 Saat - Toprak Nem Değişimi
        </h3>
        <div style={{ position: 'relative', height: '200px', display: 'flex', alignItems: 'flex-end', gap: '2px' }}>
          {/* Eşik çizgileri */}
          <div style={{ position: 'absolute', top: `${100 - (70 / maxMoisture * 100)}%`, left: 0, right: 0, borderTop: '1px dashed #2563eb', opacity: 0.4 }}>
            <span style={{ fontSize: '9px', color: '#2563eb', position: 'absolute', right: 0, top: '-12px' }}>%70 (Islak)</span>
          </div>
          <div style={{ position: 'absolute', top: `${100 - (30 / maxMoisture * 100)}%`, left: 0, right: 0, borderTop: '1px dashed #f59e0b', opacity: 0.4 }}>
            <span style={{ fontSize: '9px', color: '#f59e0b', position: 'absolute', right: 0, top: '-12px' }}>%30 (Kuru)</span>
          </div>
          {sensorHistory.map((h, i) => {
            const height = (h.moisture_pct / maxMoisture) * 100;
            const color = h.moisture_pct > 70 ? '#2563eb' : h.moisture_pct > 30 ? '#16a34a' : '#f59e0b';
            return (
              <div
                key={i}
                title={`${new Date(h.timestamp).toLocaleTimeString('tr-TR')} - %${h.moisture_pct}`}
                style={{
                  flex: 1, height: `${height}%`,
                  background: color, borderRadius: '2px 2px 0 0',
                  opacity: i === sensorHistory.length - 1 ? 1 : 0.7,
                  transition: 'height 0.3s ease'
                }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '10px', color: '#9ca3af' }}>
          <span>24 saat önce</span>
          <span>Şimdi</span>
        </div>
      </div>

      {/* Sulama Önerisi + Cihaz Detayları */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Sulama Önerisi */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '14px', color: '#1a1a2e' }}>
            AI Sulama Önerisi
          </h3>
          {liveData && (
            <div>
              <div style={{
                padding: '14px', borderRadius: '8px', marginBottom: '12px',
                background: liveData.moisture_pct < 30 ? '#fef2f2' : liveData.moisture_pct < 50 ? '#fffbeb' : '#f0fdf4',
                border: `1px solid ${liveData.moisture_pct < 30 ? '#fecaca' : liveData.moisture_pct < 50 ? '#fde68a' : '#bbf7d0'}`
              }}>
                <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '4px',
                  color: liveData.moisture_pct < 30 ? '#dc2626' : liveData.moisture_pct < 50 ? '#d97706' : '#16a34a'
                }}>
                  {liveData.moisture_pct < 30
                    ? '⚠️ Sulama Gerekli!'
                    : liveData.moisture_pct < 50
                    ? '💧 Yakında sulama önerilir'
                    : '✅ Sulama gerekmez'}
                </div>
                <div style={{ fontSize: '11px', color: '#374151' }}>
                  {liveData.moisture_pct < 30
                    ? 'Toprak nemi kritik seviyede. Hemen sulama yapılması önerilir. Tahmini ihtiyaç: 15-20 L/m²'
                    : liveData.moisture_pct < 50
                    ? 'Nem seviyesi düşmeye başladı. 2-3 saat içinde sulama planlanabilir.'
                    : 'Toprak yeterli neme sahip. Sonraki ölçüm bekleniyor.'}
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                <div style={{ marginBottom: '4px' }}>Son 24s ortalama: <strong>%{Math.round(sensorHistory.reduce((a, h) => a + h.moisture_pct, 0) / sensorHistory.length)}</strong></div>
                <div style={{ marginBottom: '4px' }}>Minimum: <strong>%{Math.round(Math.min(...sensorHistory.map(h => h.moisture_pct)))}</strong></div>
                <div>Maksimum: <strong>%{Math.round(Math.max(...sensorHistory.map(h => h.moisture_pct)))}</strong></div>
              </div>
            </div>
          )}
        </div>

        {/* Cihaz Detayları */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '14px', color: '#1a1a2e' }}>
            Cihaz Bilgileri
          </h3>
          {selectedDevice && (
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Cihaz ID', selectedDevice.device_id],
                  ['Park', selectedDevice.park_name],
                  ['Sensör Tipi', selectedDevice.sensor_type === 'soil_moisture' ? 'Kapasitif Toprak Nem' : 'Su Akış Metre'],
                  ['Firmware', selectedDevice.firmware],
                  ['Pil Voltajı', selectedDevice.battery_v ? `${selectedDevice.battery_v}V` : 'Şebeke'],
                  ['WiFi Sinyal', selectedDevice.rssi ? `${selectedDevice.rssi} dBm` : 'Ethernet'],
                  ['Son Görülme', new Date(selectedDevice.last_seen).toLocaleString('tr-TR')],
                  ['Konum', `${selectedDevice.location.lat}, ${selectedDevice.location.lng}`],
                  ['Durum', selectedDevice.status === 'online' ? '● Çevrimiçi' : '○ Çevrimdışı']
                ].map(([label, value], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '7px 0', color: '#6b7280' }}>{label}</td>
                    <td style={{ padding: '7px 0', fontWeight: '600', textAlign: 'right' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ASKİ Cloud Entegrasyonu */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginTop: '20px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#1a1a2e' }}>
          ☁️ Cloud Veri Kaynakları
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>ASKİ SOAP API</div>
            <div style={{ fontSize: '11px', color: '#16a34a', marginBottom: '4px' }}>● Aktif</div>
            <div style={{ fontSize: '10px', color: '#6b7280' }}>Günlük otomatik çekim • 3.485 abone</div>
          </div>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>ASKİ IoT (Akıllı Sayaç)</div>
            <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '4px' }}>◐ Pilot</div>
            <div style={{ fontSize: '10px', color: '#6b7280' }}>MQTT bağlantı hazır • Test aşamasında</div>
          </div>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' }}>Meteoroloji (MGM)</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>○ Planlandı</div>
            <div style={{ fontSize: '10px', color: '#6b7280' }}>Hava durumu + ET₀ hesaplama entegrasyonu</div>
          </div>
        </div>
      </div>

      {/* Entegre Edilecek Cihazlar */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginTop: '20px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '16px', color: '#1a1a2e' }}>
          Cihazlarımız
        </h3>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
          Bu sayfada görünen veriler şu an simülasyon verisidir. Aşağıdaki cihaz entegre edildiğinde gerçek zamanlı veri akışı başlayacaktır.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>ESP32 DevKit V1</span>
              <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '8px', fontWeight: '600' }}>Kurulum Bekliyor</span>
            </div>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Sensör', 'Kapasitif Toprak Nem v2.0 (Analog)'],
                  ['Bağlantı', 'WiFi → HTTPS → AWS API Gateway → Lambda'],
                  ['Veri Gönderim', 'Her 5 dakikada 1 (10 örnek ortalaması)'],
                  ['Güç', 'Li-Ion 3.7V 2000mAh (pil ile)'],
                  ['Hedef Park', '100. Yıl Parkı, Çankaya'],
                  ['Kalibrasyon', 'Havada=3600 ADC, Suda=1400 ADC'],
                  ['Firmware', 'v1.0.2 (iot/esp32_soil_moisture.ino)'],
                ].map(([label, value], i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '5px 0', color: '#6b7280', width: '120px' }}>{label}</td>
                    <td style={{ padding: '5px 0', fontWeight: '500', color: '#374151' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>Veri Akış Mimarisi</span>
            </div>
            <div style={{ fontSize: '11px', color: '#374151', lineHeight: 2 }}>
              <div><span style={{ color: '#16a34a' }}>●</span> <strong>1.</strong> ESP32 sensör okur (GPIO34, ADC)</div>
              <div><span style={{ color: '#16a34a' }}>●</span> <strong>2.</strong> 10 örnek alır → ortalamasını hesaplar</div>
              <div><span style={{ color: '#16a34a' }}>●</span> <strong>3.</strong> JSON payload oluşturur</div>
              <div><span style={{ color: '#16a34a' }}>●</span> <strong>4.</strong> WiFi üzerinden HTTPS POST gönderir</div>
              <div><span style={{ color: '#2563eb' }}>●</span> <strong>5.</strong> API Gateway alır → Lambda tetiklenir</div>
              <div><span style={{ color: '#2563eb' }}>●</span> <strong>6.</strong> Lambda doğrular → PostgreSQL'e yazar</div>
              <div><span style={{ color: '#7c3aed' }}>●</span> <strong>7.</strong> Dashboard buradan okur → canlı gösterir</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default IoTDashboard;
